<script setup lang="ts">
import { computed } from 'vue';
import { UiDialog, UiText } from '@shopexperts/shared';
import type { ExpertProfileStatus } from '@shopexperts/legacy-api';

const props = defineProps<{
  action: 'activate' | 'publish' | 'unpublish' | 'setDraft' | 'setApplication' | 'deleteExpert' | null;
  expertName: string;
  currentStatus: ExpertProfileStatus;
}>();

const open = defineModel<boolean>({ default: false });
const isLoading = defineModel<boolean>('loading', { default: false });

const emit = defineEmits<{
  confirm: [];
}>();

const actionConfig = computed(() => {
  switch (props.action) {
    case 'activate':
      return {
        title: 'Activate Application',
        description: `This will activate ${props.expertName}'s application and move them to draft status. They will receive an activation email.`,
        confirmLabel: 'Activate',
      };
    case 'publish':
      return {
        title: 'Publish Expert',
        description: `This will publish ${props.expertName}'s profile. It will be visible to clients on the marketplace.`,
        confirmLabel: 'Publish',
      };
    case 'unpublish':
      return {
        title: 'Unpublish Expert',
        description: `This will unpublish ${props.expertName}'s profile. It will no longer be visible to clients.`,
        confirmLabel: 'Unpublish',
      };
    case 'setDraft':
      return {
        title: 'Set to Draft',
        description: `This will move ${props.expertName}'s profile back to draft status.`,
        confirmLabel: 'Set Draft',
      };
    case 'setApplication':
      return {
        title: 'Set to Application',
        description: `This will move ${props.expertName}'s profile back to application status.`,
        confirmLabel: 'Set Application',
      };
    case 'deleteExpert':
      return {
        title: 'Delete Expert Account',
        description: `Delete ${props.expertName}'s expert account? This removes their profile and all associated expert users, including every agency team member. Their email addresses become available for registration again. This action cannot be undone.`,
        confirmLabel: 'Delete Expert Account',
      };
    default:
      return { title: '', description: '', confirmLabel: '' };
  }
});
</script>

<template>
  <UiDialog
    v-model="open"
    v-model:loading="isLoading"
    :title="actionConfig.title"
    :primaryActionLabel="actionConfig.confirmLabel"
    :primaryActionVariant="action === 'deleteExpert' ? 'danger' : 'primary'"
    :hideCloseButton="isLoading"
    secondaryActionLabel="Cancel"
    size="sm"
    @primaryAction="emit('confirm')"
    @secondaryAction="open = false"
    @close="open = false"
  >
    <UiText as="p" variant="p-small" :text="actionConfig.description" class="text-gray-600" />
    <UiText v-if="action === 'deleteExpert'" as="p" variant="p-small" text="External billing subscriptions and external sign-in accounts are not cancelled or deleted." class="mt-3 text-gray-600" />
    <div
      v-if="action !== 'deleteExpert'"
      class="mt-3 px-1 py-2 rounded-md bg-gray-50 border border-gray-100"
    >
      <UiText as="span" variant="caption-normal" class="text-gray-500">
        Current status:
        <span class="font-medium capitalize text-gray-800">{{ currentStatus }}</span>
      </UiText>
    </div>
  </UiDialog>
</template>
